/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useCallback } from "react";

// @mui material components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Grow from "@mui/material/Grow";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Icons
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";

const services = [
  {
    id: "performance-analytics",
    title: "Performance Analytics",
    tagline: "Benchmark. Improve. Elevate.",
    description:
      "Visualise live and historical performance. Track FCR, growth rate, and production KPIs to unlock higher efficiency.",
    status: "Development",
    statusHint: "Advanced performance analytics powered by OmnioFarm",
    icon: BarChartRoundedIcon,
  },
  {
    id: "cost-analysis",
    title: "Cost Analysis",
    tagline: "Understand your costs. Increase profitability.",
    description:
      "Analyse feed, labour, and facility costs to pinpoint inefficiencies and measure true farm profitability.",
    status: "Coming Soon",
    statusHint: "Automated cost and margin analytics",
    icon: AccountBalanceWalletRoundedIcon,
  },
  {
    id: "smart-automation",
    title: "Smart Automation",
    tagline: "Automate what matters. Analyse what counts.",
    description:
      "Connect sensors and devices to automate your facility infrastructure - intelligently and efficiently.",
    status: "Development",
    statusHint: "Smart environmental control for optimal production efficiency",
    icon: SettingsSuggestRoundedIcon,
  },
  {
    id: "marketplace",
    title: "Marketplace",
    tagline: "Your smart farming ecosystem - all in one place.",
    description:
      "Engage trusted suppliers, farm services, and digital tools designed to boost your efficiency and sustainability.",
    status: "Coming Soon",
    statusHint: "Integrated partner ecosystem driving innovation",
    icon: StorefrontRoundedIcon,
  },
];

function getStatusChipStyles(theme, status) {
  if (status === "Development") {
    return {
      bg: alpha(theme.palette.warning.light, 0.5),
      color: theme.palette.warning.dark,
      borderColor: alpha(theme.palette.warning.main, 0.28),
    };
  }

  return {
    bg: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    borderColor: alpha(theme.palette.grey[400], 0.4),
  };
}

function ServicesUpcoming() {
  const theme = useTheme();
  const handleNotify = useCallback((service) => {
    try {
      const storageKey = "omnifarm_notify_interest";
      const existingRaw = localStorage.getItem(storageKey) || "[]";
      const existing = JSON.parse(existingRaw);
      if (!existing.includes(service.id)) {
        localStorage.setItem(storageKey, JSON.stringify([...existing, service.id]));
      }
      // navigator.serviceWorker?.controller?.postMessage({ type: "QUEUE_NOTIFY_OPTIN", serviceId: service.id });
    } catch (error) {
      console.warn("Notify storage failed", error);
    }
    const subject = encodeURIComponent(`Tell me more about OmnioFarm ${service.title}`);
    const mailtoUrl = `mailto:contactus@omniofarm.com?subject=${subject}`;
    window.open(mailtoUrl, "_blank", "noopener,noreferrer");
  }, []);

  return (
    <Box
      sx={{
        maxWidth: "1100px",
        mx: "auto",
        px: { xs: 2, sm: 3 },
        pt: { xs: 1.5, md: 2 },
        pb: { xs: 3, md: 4 },
      }}
    >
      <Box mb={4}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Coming Soon to OmnioFarm
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We&apos;re continuously innovating to bring you the future of farming.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {services.map((service, index) => {
          const IconComponent = service.icon;
          const statusStyles = getStatusChipStyles(theme, service.status);

          return (
            <Grid item xs={12} sm={6} md={6} lg={4} key={service.id}>
              <Grow in style={{ transformOrigin: "0 0 0" }} timeout={350 + index * 80}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    borderColor: alpha(theme.palette.grey[300], 0.8),
                    boxShadow: "0 12px 30px -20px rgba(15, 23, 42, 0.45)",
                    transition: "box-shadow 180ms ease, transform 180ms ease",
                    "&:hover": {
                      boxShadow: "0 20px 38px -20px rgba(15, 23, 42, 0.55)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 3,
                            bgcolor: "grey.50",
                            border: `1px solid ${alpha(theme.palette.grey[300], 0.9)}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <IconComponent fontSize="small" color="primary" />
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {service.title}
                        </Typography>
                      </Stack>
                      <Chip
                        label={service.status}
                        variant="outlined"
                        size="small"
                        sx={{
                          bgcolor: statusStyles.bg,
                          color: statusStyles.color,
                          borderColor: statusStyles.borderColor,
                          fontWeight: 600,
                        }}
                      />
                    </Stack>

                    <Typography variant="subtitle2" fontStyle="italic" color="text.primary">
                      {service.tagline}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {service.description}
                    </Typography>

                    <Stack direction="row" spacing={1.5}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleNotify(service)}
                        sx={{ color: "common.white" }}
                      >
                        Notify me
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href="https://www.omniofarm.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        title={service.statusHint || "More details coming soon"}
                        sx={{
                          color: theme.palette.grey[700],
                          borderColor: alpha(theme.palette.grey[500], 0.5),
                          "&:hover": {
                            borderColor: theme.palette.grey[600],
                            backgroundColor: alpha(theme.palette.grey[100], 0.6),
                          },
                        }}
                      >
                        Learn more
                      </Button>
                    </Stack>

                    {service.statusHint ? (
                      <Typography variant="caption" color="text.secondary">
                        {service.statusHint}
                      </Typography>
                    ) : null}
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 4 }}>
        Roadmap and timelines are indicative and subject to change.
      </Typography>
    </Box>
  );
}

function Services() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={1} pb={4}>
        <ServicesUpcoming />
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Services;
